import { useState, useCallback } from 'react';
import type { LayoutNode, AppRegistry, PanelComponents } from './types';
import { updateSizes, splitPanel, removePanel, setApp, swapPanels, movePanel } from './layout-utils';
import type { DropPosition } from './layout-utils';
import { WorkspaceProvider } from './WorkspaceContext';
import { WorkspaceRenderer } from './WorkspaceRenderer';

type WorkspaceLayoutProps = {
  layout: LayoutNode;
  onLayoutChange: (layout: LayoutNode) => void;
  registry?: AppRegistry;
  components?: PanelComponents;
  workspaceId?: string;
  cwd?: string;
  promptPrefix?: string;
  interactive?: boolean;
  locked?: boolean;
  noHeader?: boolean;
  isMobile?: boolean;
  mobilePanelId?: string;
  onMobileBack?: (() => void) | null;
  onPanelResized?: () => void;
};

const noop = () => {};

export const WorkspaceLayout = ({ layout, onLayoutChange, registry, components, workspaceId, cwd, promptPrefix, interactive, locked, noHeader, isMobile, mobilePanelId, onMobileBack, onPanelResized }: WorkspaceLayoutProps) => {
  const [swapSourceId, setSwapSourceId] = useState<string | null>(null);
  const [dragSourceId, setDragSourceId] = useState<string | null>(null);
  const [maximizedPanelId, setMaximizedPanelId] = useState<string | null>(null);

  const handleSetMaximizedPanelId = useCallback(
    (id: string | null) => {
      setMaximizedPanelId(id);
      onPanelResized?.();
    },
    [onPanelResized],
  );

  const handleResized = useCallback(
    (groupId: string, sizes: number[]) => {
      onLayoutChange(updateSizes(layout, groupId, sizes));
      onPanelResized?.();
    },
    [layout, onLayoutChange, onPanelResized],
  );

  const handleSetApp = useCallback(
    (panelId: string, appType: string | null) => {
      onLayoutChange(setApp(layout, panelId, appType));
    },
    [layout, onLayoutChange],
  );

  const handleSplit = useCallback(
    (panelId: string, direction: 'horizontal' | 'vertical') => {
      onLayoutChange(splitPanel(layout, panelId, direction));
    },
    [layout, onLayoutChange],
  );

  const handleRemove = useCallback(
    (panelId: string) => {
      if (maximizedPanelId === panelId) handleSetMaximizedPanelId(null);
      onLayoutChange(removePanel(layout, panelId));
    },
    [layout, onLayoutChange, maximizedPanelId, handleSetMaximizedPanelId],
  );

  const handleSwap = useCallback(
    (sourceId: string, targetId: string) => {
      onLayoutChange(swapPanels(layout, sourceId, targetId));
      setSwapSourceId(null);
    },
    [layout, onLayoutChange],
  );

  const handleMove = useCallback(
    (sourceId: string, targetId: string, position: DropPosition) => {
      onLayoutChange(movePanel(layout, sourceId, targetId, position));
      setDragSourceId(null);
    },
    [layout, onLayoutChange],
  );

  return (
    <WorkspaceProvider value={{ workspaceId: workspaceId ?? null, cwd: cwd ?? '~', promptPrefix, swapSourceId: interactive ? swapSourceId : null, setSwapSourceId: interactive ? setSwapSourceId : noop, onSwap: interactive ? handleSwap : noop, dragSourceId: interactive ? dragSourceId : null, setDragSourceId: interactive ? setDragSourceId : noop, onMove: interactive ? handleMove : noop, maximizedPanelId: interactive ? maximizedPanelId : null, setMaximizedPanelId: interactive ? handleSetMaximizedPanelId : noop, transitioningPanelId: null, isMobile: isMobile ?? false, onMobileBack: onMobileBack ?? null }}>
      <WorkspaceRenderer
        layout={layout}
        registry={registry ?? {}}
        components={components}
        interactive={interactive}
        locked={locked}
        noHeader={noHeader}
        isMobile={isMobile}
        mobilePanelId={mobilePanelId}
        onSetApp={interactive ? handleSetApp : noop}
        onSplit={interactive ? handleSplit : noop}
        onRemove={interactive ? handleRemove : noop}
        onResized={handleResized}
      />
    </WorkspaceProvider>
  );
};
